import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import useGoogleDriveImagePicker from "../../components/useGoogleDriveImagePicker";
import { OperaContext } from "../../context/OperaContext";
import { toast } from "react-toastify";
import RichTextEditor from "../../components/RichTextEditor";

function InformationTab() {
  const { venues } = useContext(OperaContext);
  const [activeSection, setActiveSection] = useState("about");
  const [informationData, setInformationData] = useState({
    about: { headerImage: "", text: "" },
    impressum: { headerImage: "", text: "" },
    insuranceConditions: { text: "" },
    dataPrivacy: { text: "" },
    pictureCredits: { entries: [{ photo: "", courtesyOf: "" }] },
    travelConditions: { coverImage: "", text: "" },
    reviews: { entries: [] },
    FAQs: { entries: [{ question: "", answer: "" }] },
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [newReview, setNewReview] = useState({
    name: "",
    date: "",
    venue: "",
    text: "",
  });
  const [showPreview, setShowPreview] = useState(false);

  // Image picker setup
  const handleOpenPicker = useGoogleDriveImagePicker({
    isEditing: false,
    setFormData: (data) => {
      setInformationData((prev) => ({
        ...prev,
        [activeSection]: {
          ...prev[activeSection],
          ...data,
        },
      }));
    },
    setError,
  });

  // Load information data on component mount
  useEffect(() => {
    fetchInformationData();
  }, []);

  const fetchInformationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/information", {
        withCredentials: true,
      });
      if (response.data) {
        setInformationData(response.data);
      }
    } catch (error) {
      console.error("Error fetching information data:", error);
      setMessage("Error loading information data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    try {
      setLoading(true);
      await axios.put(`/api/information/${section}`, informationData[section], {
        withCredentials: true,
      });
      toast.success(`${section} updated successfully!`);
    } catch (error) {
      console.error(`Error updating ${section}:`, error);
      toast.error(`Error updating ${section}`);
    } finally {
      setLoading(false);
    }
  };

  const updateSection = (section, field, value) => {
    setInformationData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const addPictureCreditEntry = () => {
    setInformationData((prev) => ({
      ...prev,
      pictureCredits: {
        ...prev.pictureCredits,
        entries: [
          ...prev.pictureCredits.entries,
          { photo: "", courtesyOf: "" },
        ],
      },
    }));
  };

  const updatePictureCreditEntry = (index, field, value) => {
    setInformationData((prev) => ({
      ...prev,
      pictureCredits: {
        ...prev.pictureCredits,
        entries: prev.pictureCredits.entries.map((entry, i) =>
          i === index ? { ...entry, [field]: value } : entry
        ),
      },
    }));
  };

  const removePictureCreditEntry = (index) => {
    setInformationData((prev) => ({
      ...prev,
      pictureCredits: {
        ...prev.pictureCredits,
        entries: prev.pictureCredits.entries.filter((_, i) => i !== index),
      },
    }));
  };

  const removeFAQEntry = (index) => {
    setInformationData((prev) => ({
      ...prev,
      FAQs: {
        ...prev.FAQs,
        entries: prev.FAQs.entries.filter((_, i) => i !== index),
      },
    }));
  };
  const addFAQEntry = () => {
    setInformationData((prev) => ({
      ...prev,
      FAQs: {
        ...prev.FAQs,
        entries: [...prev.FAQs.entries, { question: "", answer: "" }],
      },
    }));
  };

  const updateFAQEntry = (index, field, value) => {
    setInformationData((prev) => ({
      ...prev,
      FAQs: {
        ...prev.FAQs,
        entries: prev.FAQs.entries.map((entry, i) =>
          i === index ? { ...entry, [field]: value } : entry
        ),
      },
    }));
  };

  const addReview = () => {
    if (
      !newReview.name ||
      !newReview.date ||
      !newReview.venue ||
      !newReview.text
    ) {
      setError(
        "Please fill in all review fields (Name, Date, Venue, and Review Text)"
      );
      setTimeout(() => setError(""), 3000);
      return;
    }

    const reviewEntry = {
      name: newReview.name.trim(),
      date: newReview.date,
      venue: newReview.venue,
      text: newReview.text.trim(),
    };

    setInformationData((prev) => ({
      ...prev,
      reviews: {
        ...prev.reviews,
        entries: [...prev.reviews.entries, reviewEntry],
      },
    }));

    // Reset form
    setNewReview({
      name: "",
      date: "",
      venue: "",
      text: "",
    });
    setError("");
  };

  const removeReview = (index) => {
    setInformationData((prev) => ({
      ...prev,
      reviews: {
        ...prev.reviews,
        entries: prev.reviews.entries.filter((_, i) => i !== index),
      },
    }));
  };

  const sections = [
    { key: "about", label: "Welcome" },
    { key: "impressum", label: "Imprint" },
    { key: "insuranceConditions", label: "Travel Cancellation Insurance" },
    { key: "dataPrivacy", label: "Privacy Policy" },
    { key: "pictureCredits", label: "Image Credits" },
    { key: "travelConditions", label: "Terms & Conditions" },
    { key: "reviews", label: "Testimonials" },
    { key: "FAQs", label: "FAQs" },
  ];

  return (
    <div className="p-6 bg-[#1a1a1a] text-white min-h-screen">
      <h1 className="text-3xl text-[#d4a017] mb-6">Information Management</h1>

      {message && (
        <div
          className={`p-3 mb-4 rounded ${
            message.includes("Error") ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {message}
        </div>
      )}

      {error && <div className="p-3 mb-4 rounded bg-red-600">{error}</div>}

      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2 mb-6">
        {sections.map((section) => (
          <button
            key={section.key}
            onClick={() => setActiveSection(section.key)}
            className={`px-4 py-2 rounded transition-colors ${
              activeSection === section.key
                ? "bg-[#d4a017] text-black"
                : "bg-[#333] text-white hover:bg-[#555]"
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* About Page Section */}
      {activeSection === "about" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">About Page</h2>

          <div>
            <label className="block text-sm font-medium mb-2">
              Header Image
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      updateSection("about", "headerImage", e.target.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="hidden"
                id="about-image-upload"
              />
              <label
                htmlFor="about-image-upload"
                className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors cursor-pointer flex items-center gap-2"
              >
                <img
                  src="/images/choose-file.png"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
                Choose File
              </label>
              <button
                type="button"
                onClick={() => handleOpenPicker("headerImage")}
                className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
              >
                <img
                  src="/images/drive.png"
                  alt="Google Drive"
                  className="w-4 h-4"
                />
                Google Drive
              </button>
            </div>
            {informationData.about.headerImage && (
              <div className="mt-2">
                <img
                  src={informationData.about.headerImage}
                  alt="Header preview"
                  className="max-w-xs h-32 object-cover rounded border"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Content</label>
            <button
              type="button"
              className="w-full px-4 py-2 mb-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
              onClick={() => setShowPreview((prev) => !prev)}
            >
              {showPreview ? "Edit Text" : "Edit HTML"}
            </button>
            {showPreview && (
              <textarea
                value={informationData.about.text}
                onChange={(e) => updateSection("about", "text", e.target.value)}
                rows={10}
                className="w-full p-2 bg-[#333] text-white rounded border border-[#555]"
                placeholder="Enter about page content"
              />
            )}
            {!showPreview && (
              <RichTextEditor
                value={informationData.about.text}
                height={300}
                onChange={(content) => updateSection("about", "text", content)}
              />
            )}
          </div>

          <button
            onClick={() => handleSave("about")}
            disabled={loading}
            className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save About Page"}
          </button>
        </div>
      )}

      {/* Impressum Section */}
      {activeSection === "impressum" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">Impressum</h2>

          <div>
            <label className="block text-sm font-medium mb-2">
              Header Image
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      updateSection(
                        "impressum",
                        "headerImage",
                        e.target.result
                      );
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="hidden"
                id="impressum-image-upload"
              />
              <label
                htmlFor="impressum-image-upload"
                className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors cursor-pointer flex items-center gap-2"
              >
                <img
                  src="/images/choose-file.png"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
                Choose File
              </label>
              <button
                type="button"
                onClick={() => handleOpenPicker("headerImage")}
                className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
              >
                <img
                  src="/images/drive.png"
                  alt="Google Drive"
                  className="w-4 h-4"
                />
                Google Drive
              </button>
            </div>
            {informationData.impressum.headerImage && (
              <div className="mt-2">
                <img
                  src={informationData.impressum.headerImage}
                  alt="Header preview"
                  className="max-w-xs h-32 object-cover rounded border"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Content</label>
            <button
              type="button"
              className="w-full px-4 py-2 mb-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
              onClick={() => setShowPreview((prev) => !prev)}
            >
              {showPreview ? "Edit Text" : "Edit HTML"}
            </button>
            {showPreview && (
              <textarea
                value={informationData.impressum.text}
                onChange={(e) =>
                  updateSection("impressum", "text", e.target.value)
                }
                rows={10}
                className="w-full p-2 bg-[#333] text-white rounded border border-[#555]"
                placeholder="Enter impressum content"
              />
            )}
            {!showPreview && (
              <RichTextEditor
                value={informationData.impressum.text}
                height={300}
                onChange={(content) =>
                  updateSection("impressum", "text", content)
                }
              />
            )}
          </div>

          <button
            onClick={() => handleSave("impressum")}
            disabled={loading}
            className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Impressum"}
          </button>
        </div>
      )}

      {/* Insurance Conditions Section */}
      {activeSection === "insuranceConditions" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">Insurance Conditions</h2>

          <div>
            <label className="block text-sm font-medium mb-2">Content</label>
            <button
              type="button"
              className="w-full px-4 py-2 mb-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
              onClick={() => setShowPreview((prev) => !prev)}
            >
              {showPreview ? "Edit Text" : "Edit HTML"}
            </button>
            {showPreview && (
              <textarea
                value={informationData.insuranceConditions.text}
                onChange={(e) =>
                  updateSection("insuranceConditions", "text", e.target.value)
                }
                rows={10}
                className="w-full p-2 bg-[#333] text-white rounded border border-[#555]"
                placeholder="Enter insurance conditions content"
              />
            )}
            {!showPreview && (
              <RichTextEditor
                value={informationData.insuranceConditions.text}
                height={300}
                onChange={(content) =>
                  updateSection("insuranceConditions", "text", content)
                }
              />
            )}
          </div>

          <button
            onClick={() => handleSave("insuranceConditions")}
            disabled={loading}
            className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Insurance Conditions"}
          </button>
        </div>
      )}

      {/* Data Privacy Section */}
      {activeSection === "dataPrivacy" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">Data Privacy</h2>

          <div>
            <label className="block text-sm font-medium mb-2">Content</label>
            <button
              type="button"
              className="w-full px-4 py-2 mb-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
              onClick={() => setShowPreview((prev) => !prev)}
            >
              {showPreview ? "Edit Text" : "Edit HTML"}
            </button>
            {showPreview && (
              <textarea
                value={informationData.dataPrivacy.text}
                onChange={(e) =>
                  updateSection("dataPrivacy", "text", e.target.value)
                }
                rows={10}
                className="w-full p-2 bg-[#333] text-white rounded border border-[#555]"
                placeholder="Enter data privacy content"
              />
            )}
            {!showPreview && (
              <RichTextEditor
                value={informationData.dataPrivacy.text}
                height={300}
                onChange={(content) =>
                  updateSection("dataPrivacy", "text", content)
                }
              />
            )}
          </div>

          <button
            onClick={() => handleSave("dataPrivacy")}
            disabled={loading}
            className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Data Privacy"}
          </button>
        </div>
      )}

      {/* Picture Credits Section */}
      {activeSection === "pictureCredits" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">Picture Credits</h2>

          <div className="grid grid-cols-2 gap-4 mb-4 font-semibold">
            <div>Photo</div>
            <div>Courtesy Of</div>
          </div>

          {informationData.pictureCredits.entries.map((entry, index) => (
            <div key={index} className="grid grid-cols-2 gap-4 items-center">
              <input
                type="text"
                value={entry.photo}
                onChange={(e) =>
                  updatePictureCreditEntry(index, "photo", e.target.value)
                }
                className="p-2 bg-[#333] text-white rounded border border-[#555]"
                placeholder="Photo description"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={entry.courtesyOf}
                  onChange={(e) =>
                    updatePictureCreditEntry(
                      index,
                      "courtesyOf",
                      e.target.value
                    )
                  }
                  className="flex-1 p-2 bg-[#333] text-white rounded border border-[#555]"
                  placeholder="Courtesy of"
                />
                <button
                  onClick={() => removePictureCreditEntry(index)}
                  className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div className="flex gap-4">
            <button
              onClick={addPictureCreditEntry}
              className="px-4 py-2 bg-[#555] text-white rounded hover:bg-[#666]"
            >
              Add Entry
            </button>
            <button
              onClick={() => handleSave("pictureCredits")}
              disabled={loading}
              className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save Picture Credits"}
            </button>
          </div>
        </div>
      )}
      {activeSection === "FAQs" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">FAQs</h2>
          {informationData.FAQs.entries.map((entry, index) => (
            <div key={index} className="flex flex-col gap-2">
              <div>Question</div>
              <input
                type="text"
                value={entry.question}
                onChange={(e) =>
                  updateFAQEntry(index, "question", e.target.value)
                }
                className="p-2 bg-[#333] text-white rounded border border-[#555]"
                placeholder="Question"
              />
              <div>
                <div>Answer</div>
                <textarea
                  value={entry.answer}
                  onChange={(e) =>
                    updateFAQEntry(index, "answer", e.target.value)
                  }
                  rows={5}
                  className="w-full p-2 bg-[#333] text-white rounded border border-[#555]"
                  placeholder="Answer"
                />
                <button
                  onClick={() => removeFAQEntry(index)}
                  className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div className="flex gap-4">
            <button
              onClick={addFAQEntry}
              className="px-4 py-2 bg-[#555] text-white rounded hover:bg-[#666]"
            >
              Add Entry
            </button>
            <button
              onClick={() => handleSave("FAQs")}
              disabled={loading}
              className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}
      {/* Travel Conditions Section */}
      {activeSection === "travelConditions" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">Travel Conditions</h2>

          <div>
            <label className="block text-sm font-medium mb-2">
              Cover Image
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      updateSection(
                        "travelConditions",
                        "coverImage",
                        e.target.result
                      );
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="hidden"
                id="travel-conditions-image-upload"
              />
              <label
                htmlFor="travel-conditions-image-upload"
                className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors cursor-pointer flex items-center gap-2"
              >
                <img
                  src="/images/choose-file.png"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
                Choose File
              </label>
              <button
                type="button"
                onClick={() => handleOpenPicker("coverImage")}
                className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
              >
                <img
                  src="/images/drive.png"
                  alt="Google Drive"
                  className="w-4 h-4"
                />
                Google Drive
              </button>
            </div>
            {informationData.travelConditions.coverImage && (
              <div className="mt-2">
                <img
                  src={informationData.travelConditions.coverImage}
                  alt="Cover preview"
                  className="max-w-xs h-32 object-cover rounded border"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Content</label>
            <button
              type="button"
              className="w-full px-4 py-2 mb-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
              onClick={() => setShowPreview((prev) => !prev)}
            >
              {showPreview ? "Edit Text" : "Edit HTML"}
            </button>
            {showPreview && (
              <textarea
                value={informationData.travelConditions.text}
                onChange={(e) =>
                  updateSection("travelConditions", "text", e.target.value)
                }
                rows={10}
                className="w-full p-2 bg-[#333] text-white rounded border border-[#555]"
                placeholder="Enter travel conditions content"
              />
            )}
            {!showPreview && (
              <RichTextEditor
                value={informationData.travelConditions.text}
                height={300}
                onChange={(content) =>
                  updateSection("travelConditions", "text", content)
                }
              />
            )}
          </div>

          <button
            onClick={() => handleSave("travelConditions")}
            disabled={loading}
            className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Travel Conditions"}
          </button>
        </div>
      )}

      {/* Reviews Section */}
      {activeSection === "reviews" && (
        <div className="space-y-4">
          <h2 className="text-2xl text-[#d4a017] mb-4">Reviews</h2>

          {/* Add New Review Form */}
          <div className="bg-[#333] p-4 rounded-lg border border-[#555] mb-6">
            <h3 className="text-lg text-[#d4a017] mb-4">Add New Review</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Reviewer Name
                </label>
                <input
                  type="text"
                  value={newReview.name}
                  onChange={(e) =>
                    setNewReview((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full p-2 bg-[#444] text-white rounded border border-[#555] focus:border-[#d4a017] focus:outline-none"
                  placeholder="Enter reviewer's name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Date</label>
                <input
                  type="date"
                  value={newReview.date}
                  onChange={(e) =>
                    setNewReview((prev) => ({ ...prev, date: e.target.value }))
                  }
                  className="w-full p-2 bg-[#444] text-white rounded border border-[#555] focus:border-[#d4a017] focus:outline-none"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Venue</label>
              <select
                value={newReview.venue}
                onChange={(e) =>
                  setNewReview((prev) => ({ ...prev, venue: e.target.value }))
                }
                className="w-full p-2 bg-[#444] text-white rounded border border-[#555] focus:border-[#d4a017] focus:outline-none"
              >
                <option value="">Select a venue</option>
                {venues && venues.length > 0 ? (
                  venues
                    .filter((venue) => venue && venue.name && venue._id)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((venue) => (
                      <option key={venue._id} value={venue._id}>
                        {venue.name}{" "}
                        {venue.cityNew?.name ? `- ${venue.cityNew.name}` : ""}
                      </option>
                    ))
                ) : (
                  <option value="" disabled>
                    No venues available
                  </option>
                )}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Review Text
              </label>
              <textarea
                value={newReview.text}
                onChange={(e) =>
                  setNewReview((prev) => ({ ...prev, text: e.target.value }))
                }
                rows={4}
                className="w-full p-2 bg-[#444] text-white rounded border border-[#555] focus:border-[#d4a017] focus:outline-none resize-y"
                placeholder="Enter the review text"
              />
            </div>

            <button
              onClick={addReview}
              className="px-4 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] font-medium"
            >
              Add Review
            </button>
          </div>

          {/* Existing Reviews List */}
          <div className="space-y-3">
            <h3 className="text-lg text-[#d4a017] mb-3">
              Existing Reviews ({informationData.reviews.entries.length})
            </h3>
            {informationData.reviews.entries.length === 0 ? (
              <p className="text-[#ccc] text-center py-4">
                No reviews added yet
              </p>
            ) : (
              informationData.reviews.entries.map((review, index) => {
                // Handle both old format (string) and new format (object)
                const isOldFormat = typeof review === "string";
                const reviewData = isOldFormat
                  ? { text: review, name: "Unknown", date: "", venue: "" }
                  : review;
                const venueName = reviewData.venue
                  ? venues?.find((v) => v._id === reviewData.venue)?.name ||
                    "Unknown Venue"
                  : "No venue specified";

                return (
                  <div
                    key={index}
                    className="bg-[#333] p-4 rounded-lg border border-[#555]"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-4 mb-2 text-sm text-[#ccc]">
                          <span>
                            <strong>Name:</strong>{" "}
                            {reviewData.name || "Not specified"}
                          </span>
                          {reviewData.date && (
                            <span>
                              <strong>Date:</strong>{" "}
                              {new Date(reviewData.date).toLocaleDateString()}
                            </span>
                          )}
                          <span>
                            <strong>Venue:</strong> {venueName}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeReview(index)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm ml-4"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="text-white bg-[#444] p-3 rounded border-l-4 border-[#d4a017]">
                      {reviewData.text}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex gap-4 pt-4 border-t border-[#555]">
            <button
              onClick={() => handleSave("reviews")}
              disabled={loading}
              className="px-6 py-2 bg-[#d4a017] text-black rounded hover:bg-[#b8901a] disabled:opacity-50 font-medium"
            >
              {loading ? "Saving..." : "Save All Reviews"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default InformationTab;
